import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, map, of, take } from 'rxjs';
import { UserMgmtFacade } from '../../views/user-management/facade/user-mgmt.facade';
import { LabFacade } from '../../views/labs/facade/lab.facade';
import { IUserDetail } from '../../views/user-management/models/user-mgmt';
import { returnUrlParams } from '../utils/return-url';
import { requiresLabSelection } from '../utils/lab-context';

@Injectable({
  providedIn: 'root',
})
export class LabContextGuardService {
  private readonly userMgmtFacade = inject(UserMgmtFacade);
  private readonly labFacade = inject(LabFacade);
  private readonly router = inject(Router);

  canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // The lab-selection page must always be reachable, otherwise a lab user
    // with no active lab would be redirected to it forever. Reaching it does
    // mean the active lab is being re-chosen, so scrub a selection that is not
    // one of this user's labs instead of leaving it for the header to render.
    if (state.url.startsWith('/select-lab')) {
      this.dropForeignSelection();
      return of(true);
    }

    if (!this.userMgmtFacade.getCurrentUserSignal()) {
      this.userMgmtFacade.getMeUser();
    }

    // currentUser$ only emits users that /me returned in this session. Reading
    // getMeUser directly would hand back a rehydrated user from a previous
    // account, and authorize this navigation against the wrong identity.
    return this.userMgmtFacade.currentUser$.pipe(
      take(1),
      map((user) => {
        const selectionIsValid = this.selectionBelongsTo(user);

        // Drop it before deciding, so a redirect does not leave the stale lab
        // sitting in the store for the header to keep advertising.
        if (this.labFacade.selectedLabSignal() && !selectionIsValid) {
          this.labFacade.clearSelectedLab();
        }

        if (!requiresLabSelection(user)) {
          return true;
        }
        if (selectionIsValid) {
          return true;
        }

        // Keep the page they asked for so lab selection can finish the trip.
        this.router.navigate(['/select-lab'], { queryParams: returnUrlParams(state.url) });
        return false;
      })
    );
  }

  // Clears the active lab when it is not one of the current user's, without
  // gating the caller on /me. Platform admins and data analysts are included:
  // requiresLabSelection exempts them from having to pick a lab, but a data
  // analyst still sees the header chip.
  private dropForeignSelection(): void {
    const user = this.userMgmtFacade.getCurrentUserSignal();
    if (!user || !this.labFacade.selectedLabSignal()) {
      return;
    }
    if (!this.selectionBelongsTo(user)) {
      this.labFacade.clearSelectedLab();
    }
  }

  private selectionBelongsTo(user: IUserDetail): boolean {
    const selected = this.labFacade.selectedLabSignal();
    return !!selected && (user.lab_memberships ?? []).some((lab) => lab.id === selected.id);
  }

}
